import { useState, useRef } from "react";
import { css } from "@emotion/react";
import Text from "@/base/Text";
import Header from "@/base/Header";
import Form from "@/base/Form";
import FormInput from "@/base/FormInput";
import FieldError from "@/base/FieldError";
import { buttonClick } from "@util/buttonClick";
import { em, rem } from "@util/convertUnits";
import { ButtonPill } from "../../../base";
import { US_STATES } from "@util/constants.js";
 
const RequestQuoteForm = () => {

    const [formData, setFormData] = useState({})
    const [formIsComplete, setFormIsComplete] = useState(false);
    const [showSteps, setShowSteps] = useState([])
    const stepContainers = useRef([])

    const stepCheck = (step) => {
        switch (step) {
            case 1: return !formData.role;
            case 2: return !formData.project_type;
            case 3: return !formData.door_type || formData.door_type && formData.door_type.length === 0;
            case 4: return !formData.material_type || formData.material_type && formData.material_type.length === 0;
            case 5: return !formData.timeframe;
            case 6: return !formData.message;
            case 7: return formData.first_name && formData.last_name && formData.email && formData.phone && formData.zip;
        }
    }

    const previousStepCheck = (step) => {
        let hidden = !showSteps[step + 1];
        return { display: hidden ? 'none' : 'block', paddingTop: '2rem' }
    }

    const activateStep = (step) => {
        let steps = [...showSteps];
        steps[step] = true;
        setShowSteps(steps)
        setTimeout(() => {
            stepContainers.current[step - 1].scrollIntoView(true);
        }, 500)
    }

    function stepButtonAttrs(step) {
        return {
            css: css({

                opacity: stepCheck(step) ? '0.6' : 1,
                cursor: "pointer",
                marginTop: '3rem',
                marginLeft: 'auto',
                marginRight: 'auto',
            }),
            className: 'pill pill-dark mb-5',
            onClick: stepCheck(step) ? () => { } : () => activateStep(step + 1)
        }
    }

    function stepButtonAttrsOpt(step) {
        return {
            css: css({
                opacity: 1,
                cursor: "pointer",
                marginTop: '3rem'
            }),
            className: 'pill pill-dark mb-5',
            onClick: stepCheck(step) ? () => activateStep(step + 1) : () => activateStep(step + 1)
        }
    }

    function submitAttributes() {
        let enabled = stepCheck(7)
        return {
            css: css({
                display: 'flex',
                justifyContent: 'center',
                button: {
                    display: !showSteps[7] && 'none',
                    opacity: enabled ? '1' : '0.6',
                    pointerEvents: enabled ? 'all' : 'none'
                }
            })
        }
    }

    const containerCss = css(`
        width: 100%;
        max-width: ${rem(800)};
        margin-left: auto;
        margin-right: auto;
    `);

    const formCss = css(`
        width: 100%;
        max-width: ${rem(520)};
        margin-left: auto;
        margin-right: auto;
    `);

    const smWrap = css(`
        max-width: 39.8rem;
        margin-left: auto;
        margin-right: auto;
    `);

    const stepRowCss = css(`
        position: relative;
        flex-direction: row;
        align-items: baseline;
        display: flex;
        
        div:first-of-type{
            flex-grow: 1
        }
    `);

    const previousStepCss = css(`
        font-size: .8em;

        @media (min-width: ${em(1114, 16)}){
            position: absolute;
            width: 200%;
            margin-left: -50%;
        }
        
        div {
            color: #448da4;
        }
        
    `)

    function stepRow(step) {
        return (
            <div css={stepRowCss}>
                <Text css={css({ fontSize: '0.8em' })}>Step {step + 2} / 7</Text>
                <div css={previousStepCss} tabIndex={0} role={'button'} onKeyDown={buttonClick} onClick={() => stepContainers.current[step].scrollIntoView(true)}>
                    <Text>Previous Step</Text>
                </div>
            </div>
        )
    }

    if (formIsComplete) {
        return (
            <div>
                <Header tag="h3">Thank you for your interest in LaCantina Doors.</Header>
                <Text>Your request for a quote has been submitted and you will be contacted by a LaCantina Doors Inside Sales Representative or an Authorized LaCantina Doors Dealer Representative within the next 2 business days.</Text>
                <Text>If you have additional questions, please contact us at <a href="mailto:sales@lacantinadoors.com">sales@lacantinadoors.com</a> or call us at 888-221-0141.</Text>
                <Text>
                    Sincerely,<br/>
                    <br/>
                    <strong>LaCantina Doors</strong>
                </Text>
            </div>
        );
    }

    return (
        <>
            <div css={containerCss}>
                <Text>
                    Complete the steps below, and a LaCantina Doors representative will
                    assist you.
                </Text>
            </div>
            <div css={formCss}>
                <Form name="lac_request_quote" onChange={setFormData} onSuccess={() => setFormIsComplete(true)} customSubmit={true}>

                    <div ref={(el) => stepContainers.current[0] = el}>
                        <Header tag="h3">What is your role?</Header>
                        <Text>Select an option.</Text>
                        <FieldError name={`role`} />
                        <FormInput type="radio" name="role" label="Homeowner" value="homeowner" boxed={true} />
                        <FormInput type="radio" name="role" label="Builder/Contractor" value="builder" boxed={true} />
                        <FormInput type="radio" name="role" label="Architect/Designer" value="architect" boxed={true} />
                        <FormInput type="radio" name="role" label="Dealer" value="dealer" boxed={true} />

                        <div className={'d-flex justify-content-center'} >
                            <div {...stepButtonAttrs(1)} >Next</div>
                        </div>

                    </div>

                    <div ref={(el) => stepContainers.current[1] = el} style={previousStepCheck(1)}>
                        {stepRow(0)}
                        <Header tag="h3">What type of project is it?</Header>

                        <FieldError name={`project_type`} />
                        <FormInput type="radio" name="project_type" label="New Construction" value="New Construction" boxed={true} />
                        <FormInput type="radio" name="project_type" label="Repair/Remodel" value="Repair/Remodel" boxed={true} />
                        <FormInput type="radio" name="project_type" label="Commercial" value="Commercial" boxed={true} />

                        <div className={'d-flex justify-content-center'} >
                            <div {...stepButtonAttrs(2)} >Next</div>
                        </div>
                    </div>

                    <div ref={(el) => stepContainers.current[2] = el} style={previousStepCheck(2)}>
                        {stepRow(1)}
                        <Header tag="h3">Which door types are you interested in?</Header>

                        <FieldError name={`door_type`} />
                        <FormInput type="checkbox" name="door_type" label="Folding" value="Folding Door" boxed={true} />
                        <FormInput type="checkbox" name="door_type" label="Sliding" value="Sliding Door" boxed={true} />
                        <FormInput type="checkbox" name="door_type" label="Swinging" value="Swing Door" boxed={true} />

                        <div className={'d-flex justify-content-center'} >
                            <div {...stepButtonAttrs(3)} >Next</div>
                        </div>

                    </div>

                    <div ref={(el) => stepContainers.current[3] = el} style={previousStepCheck(3)}>
                        {stepRow(2)}
                        <Header tag="h3">Do you have a preference in materials?</Header>

                        <Text>Check all that apply.</Text>
                        <FieldError name={`material_type`} />
                        <FormInput type="checkbox" name="material_type" label="V2" value="V2" boxed={true} />
                        <FormInput type="checkbox" name="material_type" label="Aluminum" value="Aluminum" boxed={true} />
                        <FormInput type="checkbox" name="material_type" label="Aluminum (Thermally Controlled)" value="Aluminum (Thermally Controlled)" boxed={true} />
                        <FormInput type="checkbox" name="material_type" label="Aluminum Wood" value="Aluminum Wood" boxed={true} />
                        <FormInput type="checkbox" name="material_type" label="Contemporary Clad" value="Contemporary Clad" boxed={true} />
                        <FormInput type="checkbox" name="material_type" label="Wood" value="Wood" boxed={true} />
                        <FormInput type="checkbox" name="material_type" label="Vinyl" value="Vinyl" boxed={true} />
                        <FormInput type="checkbox" name="material_type" label="Undecided" value="Undecided" boxed={true} />

                        <div className={'d-flex justify-content-center'} >
                            <div {...stepButtonAttrs(4)} >Next</div>
                        </div>
                    </div>


                    <div ref={(el) => stepContainers.current[4] = el} style={previousStepCheck(4)}>
                        {stepRow(3)}
                        <Header tag="h3">What is your purchasing timeframe?</Header>

                        <Text>Select an option.</Text>
                        <FieldError name={`timeframe`} />
                        <FormInput type="radio" name="timeframe" label="Less than 3 months" value="3" boxed={true} />
                        <FormInput type="radio" name="timeframe" label="6-12 months" value="6" boxed={true} />
                        <FormInput type="radio" name="timeframe" label="12+ months" value="12" boxed={true} />

                        <div className={'d-flex justify-content-center'} >
                            <div {...stepButtonAttrs(5)} >Next</div>
                        </div>
                    </div>


                    <div ref={(el) => stepContainers.current[5] = el} style={previousStepCheck(5)}>
                        {stepRow(4)}
                        <Header tag="h3">Is there anything else you&apos;d like us to know about the project?</Header>

                        <FormInput
                            type="textarea"
                            label="Message (Optional)"
                            name="message"
                            charLimit={1500}
                            // builtinError={true}
                        />
                        <div className={'d-flex justify-content-center'} >
                            <div {...stepButtonAttrsOpt(6)} >Next</div>
                        </div>
                    </div>


                    <div ref={(el) => stepContainers.current[6] = el} style={previousStepCheck(6)}>
                        {stepRow(5)}
                        <Header tag="h3">Let us know how to contact you.</Header>

                        <FormInput
                            type="text"
                            label="First Name"
                            required={true}
                            name="first_name"
                            builtinError={true}
                        />

                        <FormInput
                            type="text"
                            label="Last Name"
                            required={true}
                            name="last_name"
                            builtinError={true}
                        />

                        <FormInput
                            type="email"
                            label="Email"
                            required={true}
                            name="email"
                            builtinError={true}
                        />

                        <FormInput
                            type="phone"
                            label="Phone Number"
                            required={true}
                            name="phone"
                            builtinError={true}
                        />

                        <FormInput
                            type="select"
                            label="State"
                            required={true}
                            name="state"
                            builtinError={true}
                        >
                        {US_STATES.map((state) => (
                            <option key={state.abbrev} value={state.abbrev}>
                                {state.name}
                            </option>
                        ))}
                        </FormInput>

                        <FormInput
                            type="text"
                            label="Zip code"
                            required={true}
                            name="zip"
                            builtinError={true}
                        />

                        <div css={smWrap}>
                            <FormInput
                                type="checkbox"
                                checked={true}
                                label="Yes, I'd like to receive news and product updates from LaCantina&trade; Doors."
                                name="product_updates"

                            />
                        </div>
                    </div>
                    <div {...submitAttributes()}>
                        <ButtonPill type="submit">Submit</ButtonPill>
                    </div>
                </Form>
            </div>
        </>
    );
};

export default RequestQuoteForm;
